import "vue3-toastify/dist/index.css";
import { ref } from "vue";
import { base_url } from "../constants/config.js";
import { toast } from "vue3-toastify";
import { getCookie } from "../utils/cookie.ts";
import { useLogout } from "@/composables/useLogout.js";
import { useWalletStore } from "@/stores/walletStore.js";
// import i18n from '../utils/i18n';

// const t = (val) => i18n.global.t(val)

/**
 * A composable function for making HTTP POST requests using fetch.
 * @param {string} url - The API endpoint URL.
 * @param {Object} [options] - Optional fetch options, including headers and body.
 * @param toastError
 * @returns {object} - A reactive object containing data, error, response, and loading state.
 */
export function useFetch(url, options = {}, toastError = true) {
    const data = ref(null);
    const response = ref(null);
    const error = ref(null);
    const loading = ref(true);
    const except = ["user/authentication/logout"];
    const walletStore = useWalletStore()
    if (!options.method) {
        options.method = options.body ? "POST" : "GET"
    }

    // Default to POST method
    const fetchOptions = {
        mode: "cors",
        ...options,
    };

    fetchOptions.headers = { 'Authorization': `Bearer ${getCookie("utn") ?? ""}` }

    // Handle body stringification if present
    if (fetchOptions.body) {
        fetchOptions.body = JSON.stringify(fetchOptions.body);
        fetchOptions.headers = {
            ...fetchOptions.headers,
            "Content-Type": "application/json",
        };
    }
    if (fetchOptions.form) {
        fetchOptions.body = fetchOptions.form;
        delete fetchOptions.form;
    }

    const fetchData = async () => {
        loading.value = true;
        error.value = null;
        const target = new URL(base_url + url);
        if (options.params) {
            target.search = options.params
            if (options.params instanceof URLSearchParams)
                target.search = options.params.toString()
            if (typeof options.params === "object")
                target.search = new URLSearchParams(options.params).toString()
        }

        try {
            response.value = await fetch(target.toString(), fetchOptions);
            if (!response.value.ok) {
                // Extract and handle error data
                const errorData = await response.value.json();
                let errorMessage =
                    errorData.message || "An error occurred while fetching data.";

                if (response.value.status === 422) {
                    try {
                        errorMessage = errorMessage.map(e => e.errors.map(e => e.message).join(", ")).join("\n");
                    } catch { }
                }

                if (response.value.status === 402) {
                    walletStore.openNotification()
                } else if (toastError)
                    toast(errorMessage, {
                        theme: "auto",
                        type: "error",
                        dangerouslyHTMLString: true,
                    });
                if (
                    errorData.statusCode === 401 &&
                    except.indexOf(url) === -1
                ) {
                    useLogout();
                }
                error.value = errorData;
            } else {
                data.value = await response.value.json();
            }
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 402) {
                walletStore.openNotification()
            } else if (toastError)
                // Handle unexpected errors
                toast(err.message || "An unexpected error occurred.", {
                    theme: "auto",
                    type: "error",
                    dangerouslyHTMLString: true,
                });
            error.value = { message: err.message };
        } finally {
            loading.value = false;
        }
    };

    return new Promise((resolve) => {
        fetchData().then(() => {
            resolve({
                data: data.value,
                error: error.value,
                response: response.value,
                statusCode: response.value?.status, // Add the status code for easy access
            });
        });
    });
}


export function fetchDownload(url, options = {}) {
    const opts = {
        method: "GET",
        mode: "cors",
        headers: {},
        ...options,
    }
    opts.headers = { ...opts.headers, ...{ 'Authorization': `Bearer ${getCookie("utn") ?? ""}` } };
    const targetUrl = new URL(base_url + url);

    fetch(targetUrl.toString(), opts)
        .then(async (response) => {
            const disposition = response.headers.get('content-disposition') || ';filename=file.pdf';
            let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
            if (filename.toLowerCase().startsWith("utf-8''"))
                filename = decodeURIComponent(filename.replace("utf-8''", ''));
            else
                filename = filename.replace(/['"]/g, '');
            const url = window.URL.createObjectURL(await response.blob());
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a); // append the element to the dom
            a.click();
            a.remove();
            console.log(response);

        })
}